import { flatten, groupBy } from 'lodash';
import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { API_DATE_TIME_FORMAT, FILTERS_IDS } from '@ubeya/shared/constants';
import { SORT_BY_NAME, SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import exportExcel from '@ubeya/shared/utils/exportExcel';
import { filterByEmployees } from '@ubeya/shared/utils/filters';
import { LIST_TYPE_ENUM } from '../../scheduling/constants';
import { conditionalColumns } from './csvColumns';
import { reduceEmployeeFields } from './fields';
import { totalDuration } from 'scheduling/utils';

export const filterMainProjectsByDateForMainEventSelection = (mainProjectsByDate, filters) => {
  const filterEntry = filters?.basic?.find(({ id }) => id === FILTERS_IDS.MAIN_PROJECTS_FILTER_ID);

  const value = filterEntry?.value;
  if (!value?.length) {
    return mainProjectsByDate;
  }

  const allowedIds = new Set(value.map(Number));
  return Object.fromEntries(
    Object.entries(mainProjectsByDate || {}).map(([date, mainProjectsOnDate]) => [
      date,
      (mainProjectsOnDate || []).filter((mp) => allowedIds.has(Number(mp.id)))
    ])
  );
};

export const sortByPositionPriority = (mappedPositions, positionIdA, positionIdB) => {
  if (mappedPositions[positionIdA]?.priority === mappedPositions[positionIdB]?.priority) {
    return positionIdA - positionIdB;
  }
  return mappedPositions[positionIdA]?.priority - mappedPositions[positionIdB]?.priority;
};

const getDataByShift = ({ projects, labels, mappedPositions }) => {
  const data = labels
    .map(({ id, background: color }) => ({
      groupId: id,
      color,
      rows: [
        ...new Set(
          flatten(projects.filter(({ labelId }) => labelId === id).map(({ shifts }) => shifts)).map(
            ({ positionId }) => positionId
          )
        )
      ]
        .sort((positionIdA, positionIdB) => sortByPositionPriority(mappedPositions, positionIdA, positionIdB))
        .map((rowId) => ({ rowId }))
    }))
    .filter(({ rows }) => rows.length > 0);

  const events = projects.filter(({ labelId }) => labelId === null);

  if (events.length > 0) {
    return data.concat({ groupId: null, color: '#f4f8ff', rows: [{ rowId: null }] });
  }

  return data;
};

const getDataByPosition = ({ projects, labels, mappedPositions }) => {
  const positions = [
    ...new Set(flatten(projects.map(({ shifts }) => shifts.map(({ positionId }) => positionId))))
  ].sort((positionIdA, positionIdB) => sortByPositionPriority(mappedPositions, positionIdA, positionIdB));

  const data = positions.map((positionId) => ({
    groupId: positionId,
    rows: [{ rowId: null }, ...labels.map(({ id, background: color }) => ({ rowId: id, color }))]
  }));

  return data;
};
const getDataByClient = ({ projects, labels: locations, mappedClientBranches }) => {
  const sorter = SORT_BY_STRING('client');

  const clients = [
    ...new Set(projects.filter(({ clientBranchId }) => !!clientBranchId).map(({ clientBranchId }) => clientBranchId))
  ].sort((a, b) =>
    sorter({ client: mappedClientBranches[a.groupId] || '' }, { client: mappedClientBranches[b.groupId] || '' })
  );

  const clientsWithNoClient = projects.some(({ clientBranchId }) => !clientBranchId) ? [null, ...clients] : clients;

  const data = clientsWithNoClient.map((clientBranchId) => {
    const branchProjects = projects.filter((project) => project.clientBranchId === clientBranchId);
    const branchProjectsLocationsSet = new Set(branchProjects.map(({ locationId: id }) => id));

    const rows = locations
      .filter(({ id: locationId }) => branchProjectsLocationsSet?.has(locationId))
      .map(({ id }) => ({ rowId: id }));

    return {
      groupId: clientBranchId,
      rows
    };
  });

  return data;
};

const getDataByEmployee = ({ employeesMap, employeesData }) => {
  // If employeesData array is provided, use its order (preserves backend sort)
  // Otherwise fall back to Object.values and sort by name
  const allEmployees = employeesData
    ? employeesData.filter(({ isShift }) => isShift).map(({ id }) => id)
    : Object.values(employeesMap)
        .filter(({ isShift }) => isShift)
        .map(({ id }) => id);

  const data = allEmployees.map((employeeId) => ({
    groupId: employeeId,
    rows: [{ rowId: employeeId }]
  }));

  // Only sort by name if no employeesData was provided (fallback behavior)
  if (!employeesData) {
    data.sort((a, b) => SORT_BY_NAME(employeesMap[a.groupId], employeesMap[b.groupId]));
  }

  return data;
};

const GROUPS_MAP = {
  shift: getDataByShift,
  position: getDataByPosition,
  employee: getDataByEmployee,
  client: getDataByClient
};

export const groupByData = ({
  projects,
  labels,
  groupByKey,
  employeesMap,
  employeesData,
  mappedPositions,
  mappedClientBranches
}) => GROUPS_MAP[groupByKey]({ projects, labels, employeesMap, employeesData, mappedPositions, mappedClientBranches });

export const searchProjects = (
  search,
  projects,
  timeRangeKey,
  mappedLocations,
  mappedClientBranches,
  mappedTags,
  employeesMap
) => {
  if (!search) {
    return projects;
  }

  const filteredProjects = projects.filter(
    ({ name, locationId, shifts, clientBranchId, invoiceNumber, tags }) =>
      (name && name.toLowerCase().includes(search.toLowerCase())) ||
      (invoiceNumber && invoiceNumber.toLowerCase().includes(search.toLowerCase())) ||
      (locationId &&
        mappedLocations[locationId] &&
        mappedLocations[locationId]?.name?.toLowerCase()?.includes(search?.toLowerCase())) ||
      (clientBranchId &&
        mappedClientBranches[clientBranchId] &&
        mappedClientBranches[clientBranchId].name.toLowerCase().includes(search.toLowerCase())) ||
      (tags && mappedTags && tags.some((tag) => mappedTags[tag]?.label.toLowerCase().includes(search.toLowerCase()))) ||
      (timeRangeKey === 'weekly' ? filterByEmployees(search, shifts, employeesMap) : false)
  );

  return filteredProjects;
};

export const generateBookedEmployeesCSV = ({
  t,
  hasOrders = false,
  showTitle = false,
  title,
  shifts = [],
  employeesMap,
  accountFields,
  mappedFieldsValue,
  employeesWithFields,
  mappedPositions,
  mappedLocations,
  mappedCostCenters,
  mappedInternalShifts,
  timeFormat,
  dateFormat,
  mappedAdmins,
  projectName,
  mainProjectName = '',
  projectLocationId,
  columns,
  parsedLocalTimeFormatter,
  onExportFinished
}) => {
  const allEmployeesConfirmations = shifts.map(({ id, confirmations = [] }) => {
    const lastConfirmation = confirmations.length > 0 ? confirmations[confirmations.length - 1] : {};
    return { ...lastConfirmation, shiftId: id };
  });

  const confirmationsByEmployeeId = allEmployeesConfirmations.reduce((all, { employees = [], shiftId }) => {
    employees.forEach(({ employeeId, isConfirmed, createdAt }) => {
      if (!all[employeeId]) {
        all[employeeId] = {};
      }

      all[employeeId][shiftId] = { isConfirmed, createdAt };
    });

    return all;
  }, {});

  const visibleData = shifts.reduce(
    (
      prev,
      {
        positionId,
        date: shiftDate,
        comments,
        slots,
        locationId,
        branchCostCenterId,
        supplierAccountName,
        mappedToAnyShiftInCurrentProject = []
      }
    ) => [
      ...prev,
      ...slots
        .filter(({ employeeId }) => employeeId)
        .map(({ shiftId, employeeId, startTime, endTime, employeeSlot }) => {
          const employee = employeesMap[employeeId] || employeeSlot || {};
          const { firstName, lastName, phone, email, employeeIdField, birthdate } = employee;
          const employeeFields = reduceEmployeeFields(
            accountFields,
            mappedFieldsValue,
            employeesWithFields[employeeId]?.fields || []
          );

          const phoneUtils = phone && parsePhoneNumberFromString(`+${phone}`);
          const phoneFormatted = !phoneUtils ? '' : phoneUtils.formatInternational?.();

          let costCenter = '';
          if (branchCostCenterId) {
            costCenter = mappedCostCenters[branchCostCenterId]?.name || '';
          } else if (mappedToAnyShiftInCurrentProject?.length) {
            /*
              We're looking in shifts(which is orders shifts + internal shifts) for an internal shift
              that matches the mappedToAnyShiftInCurrentProject of the current shift (which is external).
            */

            // Find the shift that matches the ID in mappedToAnyShiftInCurrentProject
            const matchedShift = mappedInternalShifts?.[mappedToAnyShiftInCurrentProject[0]];

            if (matchedShift && matchedShift.branchCostCenterId) {
              costCenter = mappedCostCenters[matchedShift.branchCostCenterId]?.name || '';
            }
          }

          const { bookedBy, bookedAt, notifiedBy, notifiedAt } = employeeSlot?.bookingDetails || {};

          let position = '';
          if (mappedPositions[positionId]) {
            position = t(mappedPositions[positionId]?.prevSlug) ?? mappedPositions[positionId]?.slug;
          } else if (mappedToAnyShiftInCurrentProject?.length) {
            const matchedShift = mappedInternalShifts?.[mappedToAnyShiftInCurrentProject[0]];
            const internalPositionId = matchedShift?.positionId;
            position = t(mappedPositions[internalPositionId]?.prevSlug) ?? mappedPositions[internalPositionId]?.slug;
          }

          return conditionalColumns(
            {
              firstName,
              lastName,
              phone: phoneFormatted,
              email,
              employeeIdField: String(employeeIdField || ''),
              position,
              date: moment(shiftDate).format(dateFormat),
              eventName: projectName,
              mainEventName: mainProjectName || '',
              startTime: startTime ? moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat) : '',
              endTime: endTime ? moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat) : '',
              shiftArea: mappedLocations?.[locationId]?.name ?? '',
              address: mappedLocations?.[locationId]?.address ?? '',
              eventLocation: mappedLocations?.[projectLocationId]?.name ?? '',
              costCenter,
              ...(hasOrders && { supplierName: supplierAccountName || '' }),
              notes: employeeSlot?.notes || '',
              confirmationStatus: confirmationsByEmployeeId[employeeId]?.[shiftId]?.isConfirmed ? t('isConfirmed') : '',
              confirmationCreatedAt: confirmationsByEmployeeId[employeeId]?.[shiftId]?.isConfirmed
                ? parsedLocalTimeFormatter(confirmationsByEmployeeId[employeeId]?.[shiftId]?.createdAt)
                : '',
              shiftNotes: comments,
              bookedAt: bookedAt ? parsedLocalTimeFormatter(bookedAt) : '',
              bookedBy:
                bookedBy && mappedAdmins[bookedBy]
                  ? [mappedAdmins[bookedBy]?.firstName, mappedAdmins[bookedBy]?.lastName]
                      .filter((curr) => !!curr)
                      .join(' ')
                  : '',
              notifiedAt: notifiedAt ? parsedLocalTimeFormatter(notifiedAt) : '',
              notifiedBy:
                notifiedBy && mappedAdmins[notifiedBy]
                  ? [mappedAdmins[notifiedBy]?.firstName, mappedAdmins[notifiedBy]?.lastName]
                      .filter((curr) => !!curr)
                      .join(' ')
                  : '',

              age: birthdate ? moment().diff(moment(birthdate, API_DATE_TIME_FORMAT), 'years') : '',
              ...employeeFields
            },
            columns
          );
        })
    ],
    []
  );

  exportExcel({ baseData: visibleData || [], name: t('eventParticipants'), showTitle, title, onExportFinished });
};

export const generateAvailableEmployeesCSV = ({
  t,
  availableEmployees,
  availableEmployeeMap,
  showDate = false,
  shifts,
  dateFormat,
  mappedPositions,
  timeFormat,
  name
}) => {
  const visibleData = shifts.reduce(
    (all, { positionId, id, startTime, endTime }) => [
      ...all,
      ...(availableEmployees[id] || [])
        .filter((employee) => employee.listType !== LIST_TYPE_ENUM.REJECTED_APPLICATION)
        .map((employee) => ({
          [t('firstName')]: availableEmployeeMap?.[employee.id]?.firstName,
          [t('lastName')]: availableEmployeeMap?.[employee.id]?.lastName,
          ...(showDate && { [t('date')]: startTime ? moment(startTime, API_DATE_TIME_FORMAT).format(dateFormat) : '' }),
          [t('phone')]: availableEmployeeMap?.[employee.id]?.phone
            ? parsePhoneNumberFromString(`+${availableEmployeeMap?.[employee.id]?.phone}`)?.formatNational?.()
            : '',
          [t('position')]: mappedPositions[positionId]?.slug,
          [t('startTime')]: startTime ? moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat) : '',
          [t('endTime')]: endTime ? moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat) : ''
        }))
    ],
    []
  );

  exportExcel({ baseData: visibleData || [], name: t('availableEmployees'), title: name });
};

export const generateSchedulingCSV = ({
  timeFormat,
  mappedTags,
  isDetailed,
  columns,
  employeesMap,
  mappedPositions,
  t,
  data
}) => {
  if (isDetailed) {
    return generateFullSchedulingCSV({
      columns,
      timeFormat,
      mappedTags,
      mappedPositions,
      employeesMap,
      t,
      data
    });
  }

  const visibleData = (data || []).map(
    ({
      invoiceNumber,
      date,
      projectName,
      client,
      location,
      tags,
      minTime,
      maxTime,
      needed,
      booked,
      positions = []
    }) => {
      const positionsList = positions
        .filter((positionId) => !!mappedPositions?.[positionId])
        .map((positionId) => mappedPositions?.[positionId]?.label);

      return conditionalColumns(
        {
          name: projectName || '',
          date: date || '',
          client: client || '',
          location: location || '',
          tags: tags || '',
          positions: positions ? positionsList.join(', ') : '',
          invoiceId: invoiceNumber || '',
          startTime: minTime || '',
          endTime: maxTime || '',
          booked: booked || '',
          needed: needed || ''
        },
        columns
      );
    }
  );

  exportExcel({ baseData: visibleData || [], name: t('events') });
};

export const generateFullSchedulingCSV = ({
  columns,
  mappedTags,
  employeesMap,
  timeFormat,
  mappedPositions,
  t,
  data
}) => {
  const visibleData = (data || []).map(
    ({
      date,
      confirmationStatus,
      positionId,
      positionSlug,
      startTime,
      endTime,
      comments,
      projectName,
      client,
      tags,
      location,
      shiftLocation,
      employeeId,
      employeeSlot,
      isExternal
    }) => {
      const employee = employeesMap[employeeId] || employeeSlot || {};

      return conditionalColumns(
        {
          employeeIdField: employee.employeeIdField || '',
          name: projectName || '',
          date: date || '',
          client: client || '',
          location: location || '',
          comments: comments || '',

          firstName: employee.firstName,
          lastName: employee.lastName,
          phone: employee.phone ? parsePhoneNumberFromString(`+${employee.phone}`)?.formatNational?.() : '',
          email: employee.email,

          shiftLocation: shiftLocation || '',
          position: (isExternal && positionSlug ? t(positionSlug) : mappedPositions[positionId]?.slug) ?? '',
          startTime: startTime ? moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat) : '',
          endTime: endTime ? moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat) : '',
          totalTime: startTime && endTime ? totalDuration(startTime, endTime).asHours() : 0,
          tags: tags?.length > 0 ? tags.map((id) => mappedTags[id]?.label).join(', ') : '',
          notes: employeeSlot?.notes || '',
          confirmationStatus,
          employeeType: isExternal ? t('externalEmployee') : t('internalEmployee')
        },
        columns
      );
    }
  );

  exportExcel({ baseData: visibleData || [], name: t('bookedEventsEmployees') });
};

// A client's unbooking of this account's employee records no admin of ours, so it names the client instead.
export const getUnbookedByLabel = ({ t, accountId, initiatedBy, adminId, mappedAdmins, mappedClientsByAccountId }) => {
  if (initiatedBy && initiatedBy.accountId !== accountId) {
    return t('unbookedByClient', {
      clientName: initiatedBy.accountName || mappedClientsByAccountId?.[initiatedBy.accountId]?.name || ''
    });
  }

  return mappedAdmins?.[adminId] ? `${mappedAdmins[adminId].firstName} ${mappedAdmins[adminId].lastName}` : '';
};

export const getUnbookedSupplierName = ({ t, externalData, mappedSuppliers }) => {
  const { supplierAccountId, originSupplier } = externalData || {};
  const supplier = mappedSuppliers?.[supplierAccountId];

  if (originSupplier && supplier?.crmConfig?.showOriginalSupplierToClient) {
    return t('supplierOrderByMainSupplier', { originSupplier: originSupplier.name, supplier: supplier.name });
  }

  return supplier?.name || '';
};

export const generateUnbookedEmployeesCSV = ({
  accountId,
  mappedClientsByAccountId,
  mappedAdmins,
  mappedLocations,
  mappedSuppliers,
  employeesMap,
  timeFormat,
  columns,
  allShifts,
  externalUnbookings = [],
  t,
  unbookReasonsMap,
  showShiftDetails = false,
  mappedPositions = {},
  dateFormat,
  eventDetails: { locationId, name: eventName, date, endDate }
}) => {
  const externalUnbookingsByShiftId = groupBy(externalUnbookings, 'shiftId');

  // showShiftDetails (weekly board) spans several events: date, event, location and position come per shift.
  const unbookedEmployeeByShift = allShifts.map(({ unbookings, id: shiftId, startTime, endTime, position, ...shift }) =>
    [...unbookings, ...(externalUnbookingsByShiftId[shiftId] || [])].map(
      ({
        employeeId,
        unbookReasonId,
        notes,
        employeeApplicationComment,
        metadata,
        initiatedBy,
        externalData,
        type = metadata?.type,
        unbookedByAdminId = metadata?.adminId
      }) => {
        const employee = externalData?.employee ?? employeesMap[employeeId];

        return {
          name: `${employee?.firstName} ${employee?.lastName}`,
          employeeIdField: employee?.employeeIdField,
          email: employee?.email,
          position: (showShiftDetails ? mappedPositions[shift.positionId]?.label : position?.slug) || '',
          location: mappedLocations[showShiftDetails ? shift.projectLocationId : locationId]?.name || '',
          ...(showShiftDetails && {
            shiftDate: moment(startTime, API_DATE_TIME_FORMAT).format(dateFormat),
            eventName: shift.projectName || ''
          }),
          supplierName: getUnbookedSupplierName({ t, externalData, mappedSuppliers }),
          startTime: startTime && moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat),
          endTime: endTime && moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat),
          unbookReasonId,
          employeeNotes: employeeApplicationComment,
          adminNotes: notes,
          type,
          admin: getUnbookedByLabel({
            t,
            accountId,
            initiatedBy,
            adminId: unbookedByAdminId,
            mappedAdmins,
            mappedClientsByAccountId
          })
        };
      }
    )
  );

  const data = unbookedEmployeeByShift.reduce((all, currdata) => [...all, ...currdata], []);

  const projectFileName = `${eventName ? eventName : mappedLocations?.[locationId]?.name} ${date}${
    endDate ? ` - ${endDate}` : ''
  }`;

  const visibleData = (data || []).map(
    ({
      name,
      employeeIdField,
      email,
      shiftDate,
      eventName: rowEventName,
      startTime,
      endTime,
      admin,
      adminNotes,
      employeeNotes,
      unbookReasonId,
      type,
      position,
      location,
      supplierName
    }) => {
      return conditionalColumns(
        {
          name,
          employeeIdField,
          email,
          shiftDate,
          eventName: rowEventName,
          position,
          location,
          supplierName,
          startTime,
          endTime,
          admin,
          adminNotes,
          employeeNotes,
          type: t(type),
          // Both read this account's own reasons map: deleted reasons still export readably, and
          // another account's private reason never reaches the file - a propagated one is generic.
          unbookReason: unbookReasonsMap[unbookReasonId]?.label || '',
          noShow: unbookReasonsMap[unbookReasonId]?.countAsNoShow ? t('yes') : t('no')
        },
        columns
      );
    }
  );

  exportExcel({ baseData: visibleData || [], name: projectFileName });
};

export const generateForecastingReportCSV = ({ t, data, formattingOptions, accountName }) => {
  const columns = [
    { Header: t('date'), dataKey: 'date' },
    { Header: t('location'), dataKey: 'location' },
    { Header: t('eventName'), dataKey: 'eventName' },
    { Header: t('mainEventName'), dataKey: 'mainEventName' },
    { Header: t('clientName'), dataKey: 'clientName' },
    { Header: t('invoiceNumber'), dataKey: 'invoiceNumber' },
    { Header: t('tags'), dataKey: 'tags' },
    { Header: t('costCenterIdentifier'), dataKey: 'costCenterIdentifier' },
    { Header: t('costCenterName'), dataKey: 'costCenterName' },
    { Header: t('clientCostCenterIdentifier'), dataKey: 'clientCostCenterIdentifier' },
    { Header: t('clientCostCenterName'), dataKey: 'clientCostCenterName' },
    { Header: t('report__column_area'), dataKey: 'area' },
    { Header: t('position'), dataKey: 'position' },
    { Header: t('report__column_is_external'), dataKey: 'isExternal', format: 'boolean' },
    { Header: t('report__column_isApproved'), dataKey: 'isApproved', format: 'boolean' },
    { Header: t('report__column_clientApproved'), dataKey: 'isClientApproved', format: 'boolean' },
    { Header: t('report__column_isProcessed'), dataKey: 'isProcessed', format: 'boolean' },
    { Header: t('report__column_processdAt'), dataKey: 'processdAt' },
    { Header: t('report__column_agencyName'), dataKey: 'agencyName' },
    { Header: t('report__column_agencyPosition'), dataKey: 'agencyPosition' },
    { Header: t('startTimes'), dataKey: 'shiftStartTime' },
    { Header: t('endTimes'), dataKey: 'shiftEndTime' },
    { Header: t('report__column_shiftDuration'), dataKey: 'shiftDuration' },
    { Header: t('report__column_expectedBreaks'), dataKey: 'expectedBreaks' },
    { Header: t('report__column_expectedPaidHours'), dataKey: 'expectedPaidHours' },
    { Header: t('hourlyRate'), dataKey: 'hourlyRate', format: 'currency' },
    { Header: t('billRate'), dataKey: 'billRate', format: 'currency' },
    { Header: t('report__column_clientMarkup'), dataKey: 'clientMarkup', format: 'currency' },
    { Header: t('report__column_payrollFieldsExpected'), dataKey: 'payrollFieldsExpected', format: 'currency' },
    { Header: t('labourCostExpected'), dataKey: 'expectedPay', format: 'currency' },
    { Header: t('report__column_is_booked'), dataKey: 'isBooked', format: 'boolean' },
    { Header: t('report__column_firstName'), dataKey: 'employeeFirstName' },
    { Header: t('report__column_lastName'), dataKey: 'employeeLastName' },
    { Header: t('employeeIdField'), dataKey: 'employeeIdField' },
    { Header: t('report__column_timesheetStartTime'), dataKey: 'timesheetStartTime' },
    { Header: t('report__column_timesheetEndTime'), dataKey: 'timesheetEndTime' },
    { Header: t('report__column_timesheetCheckInTime'), dataKey: 'timesheetCheckInTime' },
    { Header: t('report__column_timesheetCheckOutTime'), dataKey: 'timesheetCheckOutTime' },
    { Header: t('report__column_actualDuration'), dataKey: 'actualDuration' },
    { Header: t('report__column_actualBreaks'), dataKey: 'actualBreaks' },
    { Header: t('report__column_actuaPaidHours'), dataKey: 'actualPaidHours' },
    { Header: t('report__column_payrollFieldsActual'), dataKey: 'payrollFieldsActual', format: 'currency' },
    { Header: t('labourCostActual'), dataKey: 'actualPay', format: 'currency' },
    { Header: t('report__column_payDifference'), dataKey: 'payDifference', format: 'currency' },
    { Header: t('report__column_payVariance'), dataKey: 'payVariance', format: 'percentage' }
  ];

  const visibleData = (data || []).map((item) =>
    columns.reduce((all, { Header, dataKey }) => {
      all[Header] = item[dataKey] || '';
      return all;
    }, {})
  );

  exportExcel({
    baseData: visibleData || [],
    name: [accountName, t('events')].filter(Boolean).join(' - '),
    showTitle: false,
    columnsDef: columns,
    formattingOptions
  });
};
